<template>
  <section class="antialiased" :style="{ background: backgroundSection }">
    <div class="max-w-screen-xl px-4 py-8 mx-auto lg:py-16 2xl:px-0">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="font-extrabold text-white h2" v-html="mainTitle"></h2>
      </div>

      <div class="p-4 mt-8 rounded-lg sm:p-12 bg-gray-50 ">
        <div class="grid grid-cols-1 gap-8 sm:gap-12 lg:grid-cols-2">
          <div v-for="(item, index) in items" :key="index" class="flex flex-col items-start gap-4 sm:gap-5 sm:flex-row">
            <div
              class="flex items-center justify-center w-16 h-16 mx-auto bg-gray-100 rounded-full lg:w-24 lg:h-24 shrink-0">
              <nuxt-img width="48" height="48" :class="'w-10 h-10 lg:w-12 lg:h-12'" :src="item.iconSrc"
                :alt="item.iconAlt" loading="lazy" format="webp"></nuxt-img>
            </div>
            <div>
              <h3 class="font-bold text-center h3 sm:text-left" v-html="item.title"></h3>
              <div class="mt-2 font-normal text-center textp sm:text-left" v-html="item.description"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  mainTitle: {
    type: String,
    default: "Why Cognition Mushroom Coffee Is Your Ultimate Choice:"
  },
  backgroundSection: {
    type: String,
    default: "#37736c"
  },
  items: {
    type: Array,
    default: () => [
      {
        iconSrc: "/assets/images/ELEVATED-ENERGY.svg",
        iconAlt: "Elevated Energy",
        title: "Elevated Energy",
        description: "Powered by natural caffeine, our coffee delivers a sustained energy boost, sharpening your alertness without the jitters or crash."
      },
      {
        iconSrc: "/assets/images/COGNITIVE-CLARITY.svg",
        iconAlt: "Cognitive Clarity",
        title: "Cognitive Clarity",
        description: "With Lion's Mane mushroom, fuel your brain for heightened focus and memory, making every day more productive and fulfilling."
      },
      {
        iconSrc: "/assets/images/IMMUNITY-SUPPORT.svg",
        iconAlt: "Immunity Support",
        title: "Immunity Support",
        description: "Arm your immune system with the strength of Chaga mushroom, enriched with antioxidants and beta-glucans for robust health."
      },
      {
        iconSrc: "/assets/images/ORGANIC.svg",
        iconAlt: "100% Organic",
        title: "100% Organic",
        description: "Dive into the purity of nature with our entirely organic blend, ensuring every sip supports both your health and the environment."
      },
      {
        iconSrc: "/assets/images/INNOVATIVE-FLAVOR.svg",
        iconAlt: "Innovative Flavor",
        title: "Innovative Flavor",
        description: "Indulge in the rich, bold taste of premium coffee with none of the expected mushroom flavor—perfect for coffee lovers seeking a unique and healthful twist."
      },
      {
        iconSrc: "/assets/images/LOW-ACID.svg",
        iconAlt: "Low Acid Formula",
        title: "Low Acid Formula",
        description: "Say goodbye to digestive discomfort. Our low-acid coffee means you can enjoy your beloved morning ritual without the fear of acidity-related issues."
      },
      {
        iconSrc: "/assets/images/BALANCE-WELL-BEING.svg",
        iconAlt: "Balanced Well-being",
        title: "Balanced Well-being",
        description: "Experience a positive mood and enhanced concentration thanks to the neurotransmitter-boosting power of caffeine within our blend."
      },
      {
        iconSrc: "/assets/images/SCIENCE-NATURE.svg",
        iconAlt: "Science and Nature in Harmony",
        title: "Science and Nature in Harmony",
        description: "Rely on a product that marries scientific research with natural, powerful ingredients for unparalleled cognitive and physical support."
      }
    ]
  }
});

defineBuilderComponent({
  friendlyName: 'ContentWithRoundedIcons',
  inputs: [
    { name: 'backgroundSection', type: 'color', defaultValue: '#37736c' },
    {
      name: "mainTitle",
      type: "richText",
      defaultValue: "Why Cognition Mushroom Coffee Is Your Ultimate Choice:"
    },
    {
      name: "items",
      type: "list",
      subFields: [
        {
          name: "iconSrc",
          type: "file",
          allowedFileTypes: ["jpeg", "jpg", "svg", "png"],
          defaultValue: ""
        },
        {
          name: "iconAlt",
          type: "text",
          defaultValue: ""
        },
        {
          name: "title",
          type: "richText",
          defaultValue: ""
        },
        {
          name: "description",
          type: "richText",
          defaultValue: ""
        }
      ],
      defaultValue: [
        {
          iconSrc: "/assets/images/ELEVATED-ENERGY.svg",
          iconAlt: "Elevated Energy",
          title: "Elevated Energy",
          description: "Powered by natural caffeine, our coffee delivers a sustained energy boost, sharpening your alertness without the jitters or crash."
        },
        {
          iconSrc: "/assets/images/COGNITIVE-CLARITY.svg",
          iconAlt: "Cognitive Clarity",
          title: "Cognitive Clarity",
          description: "With Lion's Mane mushroom, fuel your brain for heightened focus and memory, making every day more productive and fulfilling."
        },
        {
          iconSrc: "/assets/images/IMMUNITY-SUPPORT.svg",
          iconAlt: "Immunity Support",
          title: "Immunity Support",
          description: "Arm your immune system with the strength of Chaga mushroom, enriched with antioxidants and beta-glucans for robust health."
        },
        {
          iconSrc: "/assets/images/ORGANIC.svg",
          iconAlt: "100% Organic",
          title: "100% Organic",
          description: "Dive into the purity of nature with our entirely organic blend, ensuring every sip supports both your health and the environment."
        },
        {
          iconSrc: "/assets/images/INNOVATIVE-FLAVOR.svg",
          iconAlt: "Innovative Flavor",
          title: "Innovative Flavor",
          description: "Indulge in the rich, bold taste of premium coffee with none of the expected mushroom flavor—perfect for coffee lovers seeking a unique and healthful twist."
        },
        {
          iconSrc: "/assets/images/LOW-ACID.svg",
          iconAlt: "Low Acid Formula",
          title: "Low Acid Formula",
          description: "Say goodbye to digestive discomfort. Our low-acid coffee means you can enjoy your beloved morning ritual without the fear of acidity-related issues."
        },
        {
          iconSrc: "/assets/images/BALANCE-WELL-BEING.svg",
          iconAlt: "Balanced Well-being",
          title: "Balanced Well-being",
          description: "Experience a positive mood and enhanced concentration thanks to the neurotransmitter-boosting power of caffeine within our blend."
        },
        {
          iconSrc: "/assets/images/SCIENCE-NATURE.svg",
          iconAlt: "Science and Nature in Harmony",
          title: "Science and Nature in Harmony",
          description: "Rely on a product that marries scientific research with natural, powerful ingredients for unparalleled cognitive and physical support."
        }
      ]
    }
  ]
});
</script>